// ============================================================================
//  Tickets Phase 5-B — Composants sous-tickets, fusion, liens
//  Fichier : apps/frontend/src/pages/tickets/TicketsExtComponents.tsx
//  À importer et intégrer dans TicketDetailPage (voir PATCH-INSTRUCTIONS.md)
// ============================================================================

import React, { useState } from 'react'
import ReactDOM from 'react-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  GitBranch, Merge, Link2, Plus, X, Trash2, AlertTriangle,
  Circle, RefreshCw, CheckCircle, XCircle, Pause, ExternalLink,
  ChevronRight, Star,
} from 'lucide-react'
import { ticketsApi } from '@/modules/tickets/api/tickets.api'
import { useNotificationStore } from '@/stores/notification.store'
import { useNavigate } from 'react-router-dom'

// ─── Helpers ─────────────────────────────────────────────────────────────────

const inputCls = "w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--tx)] focus:outline-none focus:ring-1 focus:ring-[var(--blue)] placeholder:text-[var(--tx3)]"

const Spinner = () => <div className="w-4 h-4 border-2 border-[var(--blue)] border-t-transparent rounded-full animate-spin" />

const PRIORITY_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  CRITICAL: { label: 'Critique', color: 'text-[var(--red-text)]',    bg: 'bg-[#F87171]/10' },
  HIGH:     { label: 'Haute',    color: 'text-[var(--amber-text)]', bg: 'bg-[#F97316]/10' },
  MEDIUM:   { label: 'Moyenne',  color: 'text-yellow-400', bg: 'bg-yellow-400/10' },
  LOW:      { label: 'Faible',   color: 'text-[var(--tx3)]',  bg: 'bg-[var(--bd2)]/10' },
}

const STATUS_CONFIG: Record<string, { label: string; icon: typeof Circle; color: string }> = {
  OPEN:         { label: 'Ouvert',     icon: Circle,      color: 'text-[var(--blue-d)]' },
  IN_PROGRESS:  { label: 'En cours',   icon: RefreshCw,   color: 'text-[var(--amber-text)]' },
  WAITING_INFO: { label: 'En attente', icon: Pause,       color: 'text-[var(--amber-text)]' },
  IN_TEST:      { label: 'En test',    icon: CheckCircle, color: 'text-[var(--mauve)]' },
  RESOLVED:     { label: 'Résolu',     icon: CheckCircle, color: 'text-[var(--green-text)]' },
  CLOSED:       { label: 'Fermé',      icon: XCircle,     color: 'text-[var(--tx3)]' },
  CANCELLED:    { label: 'Annulé',     icon: XCircle,     color: 'text-[var(--tx4)]' },
}

const LINK_TYPE_LABELS: Record<string, string> = {
  RELATED:       'Lié à',
  DUPLICATE:     'Doublon de',
  BLOCKS:        'Bloque',
  IS_BLOCKED_BY: 'Bloqué par',
  PARENT:        'Parent',
  CHILD:         'Sous-ticket de',
}

function Modal({ title, onClose, children }: {
  title: string; onClose: () => void; children: React.ReactNode
}) {
  return (
    <div className="fixed inset-0 bg-[rgba(13,8,32,0.50)] backdrop-blur-sm flex items-center justify-center z-[99999] p-4">
      <div className="bg-white border border-[var(--bd)] rounded-2xl w-full max-w-lg shadow-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-[var(--bd)] shrink-0">
          <h2 className="text-base font-semibold text-[var(--tx)]">{title}</h2>
          <button onClick={onClose}><X className="w-4 h-4 text-[var(--tx3)] hover:text-[var(--tx)]" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-4">{children}</div>
      </div>
    </div>
  )
}

// ============================================================================
//  SOUS-TICKETS PANEL
// ============================================================================

export function SubTicketsPanel({ ticketId, canCreate }: {
  ticketId: string; canCreate: boolean
}) {
  const [showCreate, setShowCreate] = useState(false)
  const navigate    = useNavigate()
  const queryClient = useQueryClient()
  const addNotif    = useNotificationStore(s => s.addNotification)

  const { data: subTickets = [], isLoading } = useQuery({
    queryKey: ['ticket-sub', ticketId],
    queryFn:  () => ticketsApi.getSubTickets(ticketId),
  })

  return (
    <div className="nx-card p-5 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-[var(--tx)] flex items-center gap-2">
          <GitBranch className="w-4 h-4 text-[var(--tx3)]" />
          Sous-tickets
          {(subTickets as any[]).length > 0 && (
            <span className="text-xs px-1.5 py-0.5 bg-[var(--surface-2)] text-[var(--tx3)] rounded-full">
              {(subTickets as any[]).length}
            </span>
          )}
        </h3>
        {canCreate && (
          <button onClick={() => setShowCreate(true)}
            className="flex items-center gap-1 text-xs text-[var(--blue-d)] hover:text-blue-300 transition-colors">
            <Plus className="w-3.5 h-3.5" /> Créer
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-4"><Spinner /></div>
      ) : (subTickets as any[]).length === 0 ? (
        <p className="text-xs text-[var(--tx3)] py-2">Aucun sous-ticket</p>
      ) : (
        <div className="space-y-2">
          {(subTickets as any[]).map((sub: any) => {
            const sCfg = STATUS_CONFIG[sub.status]
            const SIcon = sCfg?.icon ?? Circle
            const pCfg = PRIORITY_CONFIG[sub.priority]
            return (
              <button
                key={sub.id}
                onClick={() => navigate(`/tickets/${sub.id}`)}
                className="w-full flex items-center gap-3 p-2.5 bg-[var(--surface-2)] rounded-lg hover:bg-[var(--surface-2)] transition-colors text-left"
              >
                <SIcon className={`w-3.5 h-3.5 shrink-0 ${sCfg?.color}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-[var(--tx)] truncate">{sub.title}</p>
                  <p className="text-[10px] text-[var(--tx3)]">
                    #{sub.number} · {sub.assignee ? `${sub.assignee.firstName} ${sub.assignee.lastName}` : 'Non assigné'}
                  </p>
                </div>
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full shrink-0 ${pCfg?.bg} ${pCfg?.color}`}>
                  {pCfg?.label}
                </span>
                <ChevronRight className="w-3 h-3 text-[var(--tx3)] shrink-0" />
              </button>
            )
          })}
        </div>
      )}

      {showCreate && (
        <CreateSubTicketModal
          parentId={ticketId}
          onClose={() => setShowCreate(false)}
          onCreated={(sub: any) => {
            queryClient.invalidateQueries({ queryKey: ['ticket-sub', ticketId] })
            setShowCreate(false)
            addNotif({ id: Date.now().toString(), type: 'success', title: `Sous-ticket #${sub.number} créé` })
          }}
        />
      )}
    </div>
  )
}

function CreateSubTicketModal({ parentId, onClose, onCreated }: {
  parentId: string; onClose: () => void; onCreated: (sub: any) => void
}) {
  const [form, setForm] = useState({ title: '', description: '', priority: 'MEDIUM' })
  const f = (k: string) => (e: any) => setForm(p => ({ ...p, [k]: e.target.value }))

  const { mutate, isPending } = useMutation({
    mutationFn: () => ticketsApi.createSubTicket(parentId, form),
    onSuccess: onCreated,
  })

  return (
    <Modal title="Créer un sous-ticket" onClose={onClose}>
      <div>
        <label className="block text-xs font-medium text-[var(--tx3)] mb-1.5">Titre *</label>
        <input className={inputCls} value={form.title} onChange={f('title')} placeholder="Titre du sous-ticket" />
      </div>
      <div>
        <label className="block text-xs font-medium text-[var(--tx3)] mb-1.5">Description *</label>
        <textarea className={inputCls} rows={3} value={form.description} onChange={f('description')} placeholder="Description détaillée…" />
      </div>
      <div>
        <label className="block text-xs font-medium text-[var(--tx3)] mb-1.5">Priorité</label>
        <select className={inputCls} value={form.priority} onChange={f('priority')}>
          {Object.entries(PRIORITY_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>
      <div className="flex justify-end gap-3 pt-2">
        <button onClick={onClose} className="px-4 py-2 text-sm text-[var(--tx3)] hover:text-[var(--tx)]">Annuler</button>
        <button onClick={() => mutate()} disabled={isPending || !form.title || form.description.length < 10}
          className="flex items-center gap-2 px-5 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-lg text-sm font-medium disabled:opacity-50">
          {isPending ? <Spinner /> : <Plus className="w-4 h-4" />} Créer
        </button>
      </div>
    </Modal>
  )
}

// ============================================================================
//  LIENS TICKETS PANEL
// ============================================================================

export function LinkedTicketsPanel({ ticketId, linkedTickets, linkedFrom, canEdit }: {
  ticketId: string
  linkedTickets: any[]
  linkedFrom:    any[]
  canEdit:       boolean
}) {
  const [showLink, setShowLink] = useState(false)
  const navigate    = useNavigate()
  const queryClient = useQueryClient()
  const addNotif    = useNotificationStore(s => s.addNotification)

  const unlinkMutation = useMutation({
    mutationFn: (linkedId: string) => ticketsApi.unlinkTickets(ticketId, linkedId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket', ticketId] })
      addNotif({ id: Date.now().toString(), type: 'success', title: 'Lien supprimé' })
    },
  })

  // Fusionner les deux sens en une liste
  const allLinks = [
    ...linkedTickets.filter(l => l.linkType !== 'CHILD' && l.linkType !== 'PARENT').map(l => ({
      id: l.linkedTicket?.id, ticket: l.linkedTicket, linkType: l.linkType,
    })),
    ...linkedFrom.filter(l => l.linkType !== 'CHILD' && l.linkType !== 'PARENT').map(l => ({
      id: l.mainTicket?.id, ticket: l.mainTicket, linkType: l.linkType,
    })),
  ]

  return (
    <div className="nx-card p-5 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-[var(--tx)] flex items-center gap-2">
          <Link2 className="w-4 h-4 text-[var(--tx3)]" />
          Tickets liés
          {allLinks.length > 0 && (
            <span className="text-xs px-1.5 py-0.5 bg-[var(--surface-2)] text-[var(--tx3)] rounded-full">{allLinks.length}</span>
          )}
        </h3>
        {canEdit && (
          <button onClick={() => setShowLink(true)}
            className="flex items-center gap-1 text-xs text-[var(--blue-d)] hover:text-blue-300">
            <Plus className="w-3.5 h-3.5" /> Lier
          </button>
        )}
      </div>

      {allLinks.length === 0 ? (
        <p className="text-xs text-[var(--tx3)] py-2">Aucun ticket lié</p>
      ) : (
        <div className="space-y-2">
          {allLinks.map(({ id, ticket, linkType }) => {
            if (!ticket) return null
            const sCfg = STATUS_CONFIG[ticket.status]
            const SIcon = sCfg?.icon ?? Circle
            const linkLabel = LINK_TYPE_LABELS[linkType] ?? linkType

            return (
              <div key={`${linkType}-${id}`}
                className="flex items-center gap-2 p-2.5 bg-[var(--surface-2)] rounded-lg">
                <span className="text-[10px] text-[var(--tx3)] w-20 shrink-0">{linkLabel}</span>
                <button onClick={() => navigate(`/tickets/${id}`)}
                  className="flex-1 flex items-center gap-2 text-left hover:opacity-80 min-w-0">
                  <SIcon className={`w-3 h-3 shrink-0 ${sCfg?.color}`} />
                  <span className="text-xs text-[var(--tx)] truncate">{ticket.title}</span>
                  <span className="text-[10px] text-[var(--tx3)] shrink-0">#{ticket.number}</span>
                </button>
                {canEdit && (
                  <button onClick={() => unlinkMutation.mutate(id)}
                    className="p-1 text-[var(--tx3)] hover:text-[var(--red-text)] transition-colors shrink-0">
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            )
          })}
        </div>
      )}

      {showLink && (
        <LinkTicketModal
          ticketId={ticketId}
          onClose={() => setShowLink(false)}
          onLinked={() => {
            queryClient.invalidateQueries({ queryKey: ['ticket', ticketId] })
            setShowLink(false)
            addNotif({ id: Date.now().toString(), type: 'success', title: 'Ticket lié' })
          }}
        />
      )}
    </div>
  )
}

function LinkTicketModal({ ticketId, onClose, onLinked }: {
  ticketId: string; onClose: () => void; onLinked: () => void
}) {
  const [linkedTicketId, setLinkedTicketId] = useState('')
  const [linkType, setLinkType] = useState('RELATED')
  const [search, setSearch] = useState('')

  const { data } = useQuery({
    queryKey: ['tickets-search', search],
    queryFn:  () => ticketsApi.listTickets({ search, limit: 10 }),
    enabled:  search.length >= 2,
  })
  const results = (data as any)?.data ?? []

  const { mutate, isPending } = useMutation({
    mutationFn: () => ticketsApi.linkTickets(ticketId, { linkedTicketId, linkType }),
    onSuccess:  onLinked,
  })

  return (
    <Modal title="Lier un ticket" onClose={onClose}>
      <div>
        <label className="block text-xs font-medium text-[var(--tx3)] mb-1.5">Type de lien</label>
        <select className={inputCls} value={linkType} onChange={e => setLinkType(e.target.value)}>
          {Object.entries(LINK_TYPE_LABELS)
            .filter(([k]) => !['PARENT', 'CHILD'].includes(k))
            .map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-xs font-medium text-[var(--tx3)] mb-1.5">Chercher un ticket *</label>
        <input className={inputCls} value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Tapez un titre ou numéro…" />
        {results.length > 0 && (
          <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
            {results.filter((t: any) => t.id !== ticketId).map((t: any) => (
              <button key={t.id} onClick={() => { setLinkedTicketId(t.id); setSearch(t.title) }}
                className={`w-full flex items-center gap-2 p-2 rounded-lg text-left text-xs transition-colors ${
                  linkedTicketId === t.id ? 'bg-[var(--blue)]/20 border border-blue-500/30' : 'bg-[var(--surface-2)] hover:bg-[var(--surface-3)]'
                }`}>
                <span className="text-[var(--tx3)] shrink-0">#{t.number}</span>
                <span className="text-[var(--tx)] truncate">{t.title}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="flex justify-end gap-3 pt-2">
        <button onClick={onClose} className="px-4 py-2 text-sm text-[var(--tx3)] hover:text-[var(--tx)]">Annuler</button>
        <button onClick={() => mutate()} disabled={isPending || !linkedTicketId}
          className="flex items-center gap-2 px-5 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-lg text-sm font-medium disabled:opacity-50">
          {isPending ? <Spinner /> : <Link2 className="w-4 h-4" />} Lier
        </button>
      </div>
    </Modal>
  )
}

// ============================================================================
//  BOUTON FUSION
// ============================================================================

export function MergeTicketButton({ ticketId, canMerge }: {
  ticketId: string; canMerge: boolean
}) {
  const [show, setShow] = useState(false)
  if (!canMerge) return null

  return (
    <>
      <button onClick={() => setShow(true)}
        className="flex items-center gap-2 px-3 py-2 bg-[var(--surface-2)] hover:bg-[var(--surface-3)] text-[var(--tx2)] hover:text-[var(--tx)] rounded-xl text-sm transition-colors">
        <Merge className="w-4 h-4" /> Fusionner
      </button>
      {show && <MergeModal targetId={ticketId} onClose={() => setShow(false)} />}
    </>
  )
}

function MergeModal({ targetId, onClose }: { targetId: string; onClose: () => void }) {
  const [search, setSearch]       = useState('')
  const [sourceId, setSourceId]   = useState('')
  const [sourceName, setSourceName] = useState('')
  const [reason, setReason]       = useState('')
  const [confirmed, setConfirmed] = useState(false)
  const navigate    = useNavigate()
  const queryClient = useQueryClient()
  const addNotif    = useNotificationStore(s => s.addNotification)

  const { data } = useQuery({
    queryKey: ['tickets-merge-search', search],
    queryFn:  () => ticketsApi.listTickets({ search, limit: 10 }),
    enabled:  search.length >= 2,
  })
  const results = (data as any)?.data ?? []

  const { mutate, isPending } = useMutation({
    mutationFn: () => ticketsApi.mergeTickets(targetId, { sourceId, reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket', targetId] })
      addNotif({ id: Date.now().toString(), type: 'success', title: `Ticket fusionné — #${sourceName} fermé` })
      onClose()
    },
    onError: (e: any) => {
      addNotif({ id: Date.now().toString(), type: 'error', title: e.response?.data?.message ?? 'Erreur fusion' })
    },
  })

  return (
    <Modal title="🔀 Fusionner un ticket" onClose={onClose}>
      <div className="p-3 bg-[var(--amber-bg)] border border-[rgba(245,158,11,0.25)] rounded-lg">
        <p className="text-xs text-amber-300">
          Le ticket source sera <strong>définitivement fermé</strong> et ses commentaires
          seront copiés dans ce ticket.
        </p>
      </div>

      <div>
        <label className="block text-xs font-medium text-[var(--tx3)] mb-1.5">Ticket à fusionner (source) *</label>
        <input className={inputCls} value={search} onChange={e => { setSearch(e.target.value); setSourceId('') }}
          placeholder="Rechercher par titre ou numéro…" />
        {results.length > 0 && !sourceId && (
          <div className="mt-2 space-y-1 max-h-32 overflow-y-auto">
            {results
              .filter((t: any) => t.id !== targetId && t.status !== 'CLOSED' && t.status !== 'CANCELLED')
              .map((t: any) => (
                <button key={t.id}
                  onClick={() => { setSourceId(t.id); setSourceName(`#${t.number}`); setSearch(t.title) }}
                  className="w-full flex items-center gap-2 p-2 bg-[var(--surface-2)] hover:bg-[var(--surface-3)] rounded-lg text-left text-xs">
                  <span className="text-[var(--tx3)] shrink-0">#{t.number}</span>
                  <span className="text-[var(--tx)] truncate">{t.title}</span>
                  <span className={`shrink-0 text-[10px] ${STATUS_CONFIG[t.status]?.color}`}>
                    {STATUS_CONFIG[t.status]?.label}
                  </span>
                </button>
              ))}
          </div>
        )}
      </div>

      <div>
        <label className="block text-xs font-medium text-[var(--tx3)] mb-1.5">Raison de la fusion</label>
        <input className={inputCls} value={reason} onChange={e => setReason(e.target.value)}
          placeholder="Ex: Doublon, même problème…" />
      </div>

      {sourceId && (
        <label className="flex items-start gap-2 cursor-pointer">
          <input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)}
            className="mt-0.5 accent-blue-500" />
          <span className="text-xs text-[var(--tx2)]">
            Je confirme que le ticket {sourceName} sera fermé et ses données transférées ici.
          </span>
        </label>
      )}

      <div className="flex justify-end gap-3 pt-2">
        <button onClick={onClose} className="px-4 py-2 text-sm text-[var(--tx3)] hover:text-[var(--tx)]">Annuler</button>
        <button onClick={() => mutate()} disabled={isPending || !sourceId || !confirmed}
          className="flex items-center gap-2 px-5 py-2 bg-amber-600 hover:bg-amber-500 text-[var(--tx)] rounded-lg text-sm font-medium disabled:opacity-50">
          {isPending ? <Spinner /> : <Merge className="w-4 h-4" />} Fusionner
        </button>
      </div>
    </Modal>
  )
}

// ============================================================================
//  SATISFACTION WIDGET (affiché sur tickets CLOSED/RESOLVED)
// ============================================================================

export function SatisfactionWidget({ ticket }: { ticket: any }) {
  const [submitted, setSubmitted] = useState(false)
  const [hovered, setHovered]     = useState(0)
  const addNotif = useNotificationStore(s => s.addNotification)

  const { mutate, isPending } = useMutation({
    mutationFn: (score: number) => ticketsApi.submitSatisfaction(ticket.id, { score }),
    onSuccess: () => {
      setSubmitted(true)
      addNotif({ id: Date.now().toString(), type: 'success', title: 'Merci pour votre avis !' })
    },
  })

  if (!['CLOSED', 'RESOLVED'].includes(ticket.status)) return null

  // Déjà noté
  if (ticket.satisfactionScore || submitted) {
    const score = ticket.satisfactionScore ?? 0
    const emojis = ['😞','😕','😐','😊','😄']
    return (
      <div className="nx-card p-5 shadow-sm">
        <p className="text-xs font-semibold text-[var(--tx3)] mb-2 flex items-center gap-2">
          <Star className="w-3.5 h-3.5" /> Satisfaction
        </p>
        <div className="flex items-center gap-2">
          <span className="text-2xl">{emojis[score - 1] ?? '—'}</span>
          <span className="text-sm font-bold text-[var(--tx)]">{score}/5</span>
          {ticket.satisfactionNote && (
            <p className="text-xs text-[var(--tx3)] italic ml-2">"{ticket.satisfactionNote}"</p>
          )}
        </div>
      </div>
    )
  }

  const emojis = ['😞','😕','😐','😊','😄']
  return (
    <div className="nx-card p-5 shadow-sm">
      <p className="text-xs font-semibold text-[var(--tx3)] mb-3 flex items-center gap-2">
        <Star className="w-3.5 h-3.5" /> Évaluer ce ticket
      </p>
      <div className="flex items-center gap-2">
        {[1,2,3,4,5].map(score => (
          <button key={score}
            onClick={() => mutate(score)}
            onMouseEnter={() => setHovered(score)}
            onMouseLeave={() => setHovered(0)}
            disabled={isPending}
            className={`text-2xl transition-transform hover:scale-125 disabled:opacity-50 ${
              hovered >= score ? 'opacity-100' : 'opacity-50'
            }`}
            title={`${score}/5`}
          >
            {emojis[score - 1]}
          </button>
        ))}
      </div>
    </div>
  )
}
